import React, { useState } from 'react';
import { Badge, Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { ArrowRight, ChartBar, ListDashes } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { AnalyticsModal } from '../../../components/analytics';
import { DataGrid } from '../../../components/datagrid';
import { ModuleMaterialsManager } from '../../../components/ModuleMaterialsManager';
import { PageTitle } from '../../../components/PageTitle';
import { RowActionButton } from '../../../components/buttons/RowActionButton';
import { SearchFilter } from '../../../components/inputs/filters/search';
import { useFetch } from '../../../hooks/useFetch';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { useAuth } from '../../../providers/auth';
import { api } from '../../../services/api';
import { getCellStatus } from '../../../utils/FormatFunctions';

interface MaterialCounts {
  total: number;
  public: number;
  hidden: number;
}

function ModuleCounts({ moduleId, refreshKey }: { moduleId: number; refreshKey: number }) {
  const [counts, setCounts] = React.useState<MaterialCounts | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    setCounts(null);
    setFailed(false);
    api
      .get(`/v1/admin/extra_materials/modules/${moduleId}/counts`)
      .then((response) => {
        if (active) {
          setCounts(response.data?.data || { total: 0, public: 0, hidden: 0 });
        }
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });
    return () => {
      active = false;
    };
  }, [moduleId, refreshKey]);

  if (failed) {
    return (
      <Text fontSize="12px" color="state.destructive">
        Erro ao carregar
      </Text>
    );
  }
  if (!counts) {
    return <Spinner size="xs" color="content.subtle" />;
  }
  return (
    <Flex gap="4px" wrap="wrap" data-testid={`module-counts-${moduleId}`}>
      <Badge variant="neutral">{counts.total} total</Badge>
      <Badge variant="success">{counts.public} públicos</Badge>
      {counts.hidden > 0 && <Badge variant="warning">{counts.hidden} ocultos</Badge>}
    </Flex>
  );
}

export function ContentsModulesPage() {
  usePageTracking('modules_list');
  const [selectedAnalyticsModule, setSelectedAnalyticsModule] = useState<number | null>(null);
  const [selectedMaterialsModule, setSelectedMaterialsModule] = useState<any>(null);
  const [countsRefreshKey, setCountsRefreshKey] = useState(0);
  const baseUrl = '/v1/admin/classes';
  const { requestApi, data, loading, error } = useFetch(baseUrl);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trackModuleView } = useAnalytics();
  const groupId = user?.group?.id;
  const canManageMaterials = groupId === 1 || groupId === 2;

  const columns = [
    { name: 'id', label: 'Id' },
    { name: 'name', label: 'Nome' },
    { name: 'status', label: 'Status' },
    ...(canManageMaterials ? [{ name: 'material_counts', label: 'Materiais' }] : []),
  ];

  const openAnalytics = (moduleId: number) => {
    trackModuleView(moduleId);
    setSelectedAnalyticsModule(moduleId);
  };

  return (
    <Box>
      <PageTitle description="Estruture os módulos de conteúdo em semanas e encontros.">
        Módulos
      </PageTitle>
      <DataGrid
        columns={columns}
        rows={data?.data}
        requestApi={requestApi}
        error={error}
        baseUrl={baseUrl}
        data={data}
        hasCreate={groupId !== 7 && groupId !== 3 && groupId !== 6}
        loading={loading}
        rowActions={{
          show: false,
          edit: groupId !== 7 && groupId !== 6,
          delete: groupId !== 7 && groupId !== 6 && groupId !== 3,
        }}
        columnWithAction={
          canManageMaterials
            ? [
                {
                  name: 'material_counts',
                  columnAction: (rowItem: any) => (
                    <ModuleCounts moduleId={rowItem.id} refreshKey={countsRefreshKey} />
                  ),
                },
              ]
            : []
        }
        rowExtraActions={(rowItem: any) => (
          <>
            {canManageMaterials && (
              <RowActionButton
                label="Ordem e visibilidade dos materiais"
                icon={<ListDashes size={16} />}
                onClick={() => setSelectedMaterialsModule(rowItem)}
                data-testid={`manage-materials-${rowItem.id}`}
              />
            )}
            <RowActionButton
              label="Estatísticas"
              icon={<ChartBar size={16} />}
              onClick={() => openAnalytics(rowItem.id)}
            />
            <RowActionButton
              label="Ver semanas"
              icon={<ArrowRight size={16} />}
              onClick={() => navigate(`${rowItem.id}/weeks`)}
              data-testid={`view-weeks-${rowItem.id}`}
            />
          </>
        )}
        formatColum={[{ field: 'status', formatField: getCellStatus }]}
        headerFilters={[{ name: 'search', filterComponent: SearchFilter }]}
      />
      <AnalyticsModal
        isOpen={selectedAnalyticsModule !== null}
        onClose={() => setSelectedAnalyticsModule(null)}
        moduleId={selectedAnalyticsModule || undefined}
        type="module"
      />
      {canManageMaterials && (
        <ModuleMaterialsManager
          isOpen={selectedMaterialsModule !== null}
          moduleId={selectedMaterialsModule?.id || null}
          moduleName={selectedMaterialsModule?.name || ''}
          onClose={() => setSelectedMaterialsModule(null)}
          onCountsChanged={() => setCountsRefreshKey((current) => current + 1)}
        />
      )}
    </Box>
  );
}
